import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ProfileSection from './components/ProfileSection';
import SecuritySection from './components/SecuritySection';
import NotificationSection from './components/NotificationSection';
import ApiKeySection from './components/ApiKeySection';
import PreferencesSection from './components/PreferencesSection';
import AuditLogSection from './components/AuditLogSection';
import Icon from '../../components/AppIcon';

const AccountSettings = () => {
  const [activeTab, setActiveTab] = useState('profile');

  // Mock user data
  const currentUser = {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@cyberguard.gov",
    role: "Senior Security Analyst",
    department: "Incident Response Team",
    permissions: ["view_dashboard", "view_incidents", "create_incident", "view_reports", "view_settings"]
  };

  // Mock incident counts for header
  const incidentCounts = {
    critical: 3,
    high: 8,
    medium: 15,
    low: 24
  };

  const settingsTabs = [
    {
      id: 'profile',
      label: 'Profile',
      icon: 'User',
      description: 'Personal information and contact details'
    },
    {
      id: 'security',
      label: 'Security',
      icon: 'Shield',
      description: 'Password, 2FA, and session management'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: 'Bell',
      description: 'Email, Slack, and SMS preferences'
    },
    {
      id: 'api-keys',
      label: 'API Keys',
      icon: 'Key',
      description: 'Manage integration API keys'
    },
    {
      id: 'preferences',
      label: 'Preferences',
      icon: 'Settings',
      description: 'Language, timezone, and display options'
    },
    {
      id: 'audit-log',
      label: 'Audit Log',
      icon: 'FileText',
      description: 'Account activity history'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSection />;
      case 'security':
        return <SecuritySection />;
      case 'notifications':
        return <NotificationSection />;
      case 'api-keys':
        return <ApiKeySection />;
      case 'preferences':
        return <PreferencesSection />;
      case 'audit-log':
        return <AuditLogSection />;
      default:
        return <ProfileSection />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={currentUser} incidentCounts={incidentCounts} />
      <main className="nav-offset">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
                <Icon name="User" size={32} color="white" />
              </div>
              <div>
                <h1 className="text-3xl font-semibold text-foreground">Account Settings</h1>
                <p className="text-lg text-muted-foreground">
                  Manage your profile, security, and preferences
                </p>
              </div>
            </div>
            
            {/* User Info Card */}
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={24} color="white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-foreground">{currentUser?.name}</h2>
                    <p className="text-sm text-muted-foreground">{currentUser?.email}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="px-2 py-1 text-xs font-medium bg-primary text-primary-foreground rounded-full">
                        {currentUser?.role}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium bg-muted text-muted-foreground rounded-full">
                        {currentUser?.department}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Last login</p>
                  <p className="text-sm font-medium text-foreground">Today at 8:30 AM</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Settings Navigation - Desktop */}
            <div className="hidden lg:block">
              <div className="bg-card rounded-lg border border-border p-4">
                <h3 className="font-medium text-foreground mb-4">Settings</h3>
                <nav className="space-y-1">
                  {settingsTabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left transition-colors duration-200 ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <div>
                        <p className="font-medium">{tab?.label}</p>
                        <p className={`text-xs ${activeTab === tab?.id ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                          {tab?.description}
                        </p>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Settings Navigation - Mobile */}
            <div className="lg:hidden mb-6">
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex overflow-x-auto space-x-2 pb-2">
                  {settingsTabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-md whitespace-nowrap transition-colors duration-200 ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span className="font-medium">{tab?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Settings Content */}
            <div className="lg:col-span-3">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountSettings;